import styles from './ApplicationMainDrawerMenu.module.css'
import { useState } from 'react'
import { SxProps } from '@mui/material'
import { NavLink, useNavigate } from 'react-router-dom'
import { useReadMonitorsShortQuery } from 'services/monitor'
import { useReadMonitorRunCountQuery } from 'services/monitorRun'
import NotificationsIcon from '@mui/icons-material/Notifications'
import ShareIcon from '@mui/icons-material/Share'
import { ShareDialog } from 'components/ShareDialog'
import type { ReadMonitorRunCountParams } from 'services/monitorRun'
import { useReadPropertySpyListQuery } from 'services/propertySpy'
import { PropertyIcon } from 'shared/icons/PropertyIcon'
import { MarketIcon } from 'shared/icons/MarketIcon'
import { CompetitorsIcon } from 'shared/icons/CompetitorsIcon'


import { NotificationIcon } from 'shared/icons/NotificationIcon'
import { useAppDispatch, useAppSelector } from 'shared/hooks/store'
import { selectMode, selectParentUrl, setMode } from 'store/applicationSlice'

interface ApplicationMainDrawerMenuProps {
}

export const ApplicationMainDrawerMenu = (): JSX.Element => {  
    const dispatch = useAppDispatch()
    const parentUrl = useAppSelector(selectParentUrl)
    const mode = useAppSelector(selectMode)
    const navigate = useNavigate()

    const getLinkClassName = (options: any, url: string) => {
        const {
            isActive
        } = options

        const classes: string[] = [styles.ApplicationMainDrawerMenu__Link]

        if ((isActive && !openShareDialog) || (parentUrl === url && !openShareDialog)) {
            classes.push(styles.ApplicationMainDrawerMenu__Link__Active)
        }

        return classes.join(' ')
    }

    const {
        data: readPropertySpyListQueryData,
        isLoading: readPropertySpyListIsLoading,
        isFetching: readPropertySpyListIsFetching
    } = useReadPropertySpyListQuery({});

    const [open, setOpen] = useState(true)
    const [openShareDialog, setOpenShareDialog] = useState(false)

    const styleShareButton = {
        color: '#FFF',
        backgroundColor: 'success.light',
        '&:hover': {
            backgroundColor: 'success.dark'
        }
    }

    const handleClickShareButton = () => {
        setOpenShareDialog(true);
    }

    const handleCancelShareDialog = () => {
        setOpenShareDialog(false);
    }

    const iconStyle: SxProps = {
        fontSize: '36px',
        fill: 'none',
        color: '#FFF'
    }

    const iconShareStyle: SxProps = {
        fontSize: '36px',
        color: '#FFF',
    }

    const handleToMarketSpy = () => {
        dispatch(setMode('market'))
        navigate("/market");
    }

    const handleToPropertySpy = () => {
        dispatch(setMode('property'))
        navigate("/");
    }

    let content = null

    if (mode === 'property') {
        content = (
            <div className={styles.ApplicationMainDrawerMenu__Root}>
                <ul className={styles.ApplicationMainDrawerMenu__List}>
                    <li className={styles.ApplicationMainDrawerMenu__ListItem}>
                        <NavLink
                            className={(params) => getLinkClassName(params, `/competitors`)}
                            to={'/competitors'}
                        >
                            <div className={`${styles.ApplicationMainDrawerMenu__Item} ${styles.ApplicationMainDrawerMenuItem}`} >
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__IconContainer}
                                >
                                    <PropertyIcon 
                                        sx={iconStyle}
                                    />
                                </div>
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__Caption}
                                >
                                    Properties
                                </div>
                            </div>
                        </NavLink>
                    </li>
                    <li className={styles.ApplicationMainDrawerMenu__ListItem}>
                        <NavLink
                            className={(params) => getLinkClassName(params, `/property`)}
                            to={'/property'}
                        >
                            <div className={`${styles.ApplicationMainDrawerMenu__Item} ${styles.ApplicationMainDrawerMenuItem}`} >
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__IconContainer}
                                >
                                    <CompetitorsIcon
                                        sx={iconStyle}
                                    />
                                </div>
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__Caption}
                                >
                                    Competitors
                                </div>
                            </div>
                        </NavLink>
                    </li>
                    <li className={styles.ApplicationMainDrawerMenu__ListItem}>
                        <NavLink
                            className={(params) => getLinkClassName(params, `/notifications`)}
                            to={'/notifications'}
                        >
                            <div className={`${styles.ApplicationMainDrawerMenu__Item} ${styles.ApplicationMainDrawerMenuItem}`} >
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__IconContainer}
                                >
                                    <NotificationIcon
                                        sx={iconStyle}
                                    />
                                </div>
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__Caption}
                                >
                                    Notifications
                                </div>
                            </div>
                        </NavLink>
                    </li>
                    <li className={styles.ApplicationMainDrawerMenu__ListItem}>
                        <button
                            className={`${styles.ApplicationMainDrawerMenu__Button} ${styles.ApplicationMainDrawerMenu__Button__Share}`}
                            onClick={handleClickShareButton}
                        >
                            <div className={`${styles.ApplicationMainDrawerMenu__Item} ${styles.ApplicationMainDrawerMenuItem}`} >
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__IconContainer}
                                >
                                    <ShareIcon
                                        sx={iconStyle}
                                    />
                                </div>
                                <div
                                    className={`${styles.ApplicationMainDrawerMenuItem__Caption} ${styles.ApplicationMainDrawerMenuItem__Caption__Share}`}
                                >
                                    Share App
                                </div>
                            </div>
                        </button>
                    </li>
                </ul>
            </div>
        )
    }

    if (mode === 'market') {
        content = (
            <div className={styles.ApplicationMainDrawerMenu__Root}>
                <ul className={styles.ApplicationMainDrawerMenu__List}>  
                    <li className={styles.ApplicationMainDrawerMenu__ListItem}>
                        <NavLink
                            className={(params) => getLinkClassName(params, `/market`)}
                            to={'/market'}
                        >
                            <div className={`${styles.ApplicationMainDrawerMenu__Item} ${styles.ApplicationMainDrawerMenuItem}`} >
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__IconContainer}
                                >
                                    <MarketIcon
                                        sx={iconStyle}
                                    />
                                </div>
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__Caption}
                                >
                                    Market
                                </div>
                            </div>
                        </NavLink>
                    </li>
                    <li className={styles.ApplicationMainDrawerMenu__ListItem}>
                        <NavLink
                            className={(params) => getLinkClassName(params, `/notifications`)}
                            to={'/notifications'}
                        >
                            <div className={`${styles.ApplicationMainDrawerMenu__Item} ${styles.ApplicationMainDrawerMenuItem}`} >
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__IconContainer}
                                >
                                    <NotificationIcon
                                        sx={iconStyle}
                                    />
                                </div>
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__Caption}
                                >
                                    Notifications
                                </div>
                            </div>
                        </NavLink>
                    </li>
                    <li className={styles.ApplicationMainDrawerMenu__ListItem}>
                        <button
                            className={`${styles.ApplicationMainDrawerMenu__Button} ${styles.ApplicationMainDrawerMenu__Button__Share}`}
                            onClick={handleClickShareButton}
                        >
                            <div className={`${styles.ApplicationMainDrawerMenu__Item} ${styles.ApplicationMainDrawerMenuItem}`} >
                                <div
                                    className={styles.ApplicationMainDrawerMenuItem__IconContainer}
                                >
                                    <ShareIcon
                                        sx={iconStyle}
                                    />
                                </div>
                                <div
                                    className={`${styles.ApplicationMainDrawerMenuItem__Caption} ${styles.ApplicationMainDrawerMenuItem__Caption__Share}`}
                                >
                                    Share App
                                </div>
                            </div>
                        </button>
                    </li>
                </ul>
            </div>
        )
    }

    return (
        <>
            {content}
          
            <ShareDialog
                keepMounted={true}
                open={openShareDialog}
                onClose={handleCancelShareDialog}
                onCancel={handleCancelShareDialog}
            />
        </>
    );
}